import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Slider } from "../components/ui/slider";
import { 
  Activity, TrendingUp, TrendingDown, Minus, Plus, RefreshCw,
  Heart, Frown, Battery, Wind, AlertCircle, Utensils, Moon, HelpCircle, Smile,
  Calendar, ChevronRight, Save
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const SYMPTOM_CONFIG = {
  pain: { name: "Pain", icon: Activity, color: "#ef4444" },
  nausea: { name: "Nausea", icon: Frown, color: "#f97316" },
  fatigue: { name: "Fatigue", icon: Battery, color: "#eab308" },
  breathing: { name: "Shortness of Breath", icon: Wind, color: "#3b82f6" },
  anxiety: { name: "Anxiety", icon: AlertCircle, color: "#8b5cf6" },
  appetite: { name: "Appetite", icon: Utensils, color: "#22c55e" },
  sleep: { name: "Sleep Quality", icon: Moon, color: "#6366f1" },
  mood: { name: "Overall Mood", icon: Smile, color: "#ec4899" }
};

const SymptomDiaryEnhanced = () => {
  const [activeTab, setActiveTab] = useState('log');
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [trends, setTrends] = useState(null);
  const [currentSymptoms, setCurrentSymptoms] = useState({
    pain: 0, nausea: 0, fatigue: 0, breathing: 0,
    anxiety: 0, appetite: 5, sleep: 5, mood: 5
  });
  const [notes, setNotes] = useState('');
  const { toast } = useToast();
  
  const token = localStorage.getItem('token');

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase47/symptom-diary/history?days=14`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.entries || []);
      }
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, [token]);

  const fetchTrends = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase47/symptom-diary/trends?days=14`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTrends(data);
      }
    } catch (err) {
      console.error('Error fetching trends:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
    fetchTrends();
  }, [fetchHistory, fetchTrends]);

  const logSymptoms = async () => {
    try {
      const res = await fetch(`${API}/api/phase47/symptom-diary/log`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          symptoms: currentSymptoms,
          notes
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        toast({
          title: "Symptoms Logged",
          description: data.alerts?.length > 0 
            ? "Your symptoms have been recorded. Please review the alerts." 
            : "Your symptoms have been recorded successfully."
        });
        setCurrentSymptoms({
          pain: 0, nausea: 0, fatigue: 0, breathing: 0,
          anxiety: 0, appetite: 5, sleep: 5, mood: 5
        });
        setNotes('');
        fetchHistory();
        fetchTrends();
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to log symptoms", variant: "destructive" });
    }
  };

  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improving': return <TrendingDown className="h-4 w-4 text-green-500" />;
      case 'worsening': return <TrendingUp className="h-4 w-4 text-red-500" />;
      default: return <Minus className="h-4 w-4 text-gray-400" />;
    }
  };

  const getSeverityColor = (value) => {
    if (value <= 3) return 'text-green-600';
    if (value <= 6) return 'text-amber-600';
    return 'text-red-600';
  };

  const chartData = history.slice(0, 14).reverse().map(entry => ({
    date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    ...entry.symptoms
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="symptom-diary-enhanced">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="h-7 w-7 text-teal-600" />
            Symptom Diary
          </h1>
          <p className="text-gray-600">Track your daily symptoms and see trends over time</p>
        </div>
      </div>

      <div className="flex gap-2 border-b pb-2">
        <Button variant={activeTab === 'log' ? 'default' : 'ghost'} onClick={() => setActiveTab('log')} className={activeTab === 'log' ? 'bg-teal-600' : ''}>
          <Plus className="h-4 w-4 mr-2" />Log Today
        </Button>
        <Button variant={activeTab === 'trends' ? 'default' : 'ghost'} onClick={() => setActiveTab('trends')} className={activeTab === 'trends' ? 'bg-teal-600' : ''}>
          <TrendingUp className="h-4 w-4 mr-2" />Trends
        </Button>
        <Button variant={activeTab === 'history' ? 'default' : 'ghost'} onClick={() => setActiveTab('history')} className={activeTab === 'history' ? 'bg-teal-600' : ''}>
          <Calendar className="h-4 w-4 mr-2" />History
        </Button>
      </div>

      {activeTab === 'log' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>How are you feeling today?</CardTitle>
              <CardDescription>Rate each symptom from 0 (none) to 10 (severe)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {Object.entries(SYMPTOM_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                return (
                  <div key={key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 font-medium">
                        <Icon className="h-5 w-5" style={{ color: config.color }} />
                        {config.name}
                      </label>
                      <span className={`font-bold text-lg ${getSeverityColor(currentSymptoms[key])}`}>{currentSymptoms[key]}/10</span>
                    </div>
                    <Slider value={[currentSymptoms[key]]} onValueChange={(value) => setCurrentSymptoms({ ...currentSymptoms, [key]: value[0] })} max={10} step={1} className="w-full" />
                    <div className="flex justify-between text-xs text-gray-400">
                      <span>None</span><span>Moderate</span><span>Severe</span>
                    </div>
                  </div>
                );
              })}
              <div className="pt-4">
                <label className="font-medium">Additional Notes</label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Any other observations, triggers, or things that helped..." rows={3} className="mt-2" />
              </div>
              <Button onClick={logSymptoms} className="w-full bg-teal-600 hover:bg-teal-700">
                <Save className="h-4 w-4 mr-2" />Save Today's Entry
              </Button>
            </CardContent>
          </Card>
          {Object.entries(currentSymptoms).some(([_, v]) => v >= 7) && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-900">High Symptom Alert</h4>
                    <p className="text-sm text-amber-700">You've reported one or more symptoms at level 7 or higher. Consider contacting your care team if symptoms persist.</p>
                    <Button variant="link" className="text-amber-700 p-0 h-auto mt-1">Call Nurse Line: 703-578-7108</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {activeTab === 'trends' && trends && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card><CardContent className="p-4 text-center"><p className="text-sm text-gray-500">Overall Burden</p><p className={`text-3xl font-bold ${getSeverityColor(trends.summary?.overall_symptom_burden || 0)}`}>{trends.summary?.overall_symptom_burden || 0}/10</p></CardContent></Card>
            <Card className="bg-green-50 border-green-200"><CardContent className="p-4 text-center"><p className="text-sm text-green-600">Improving</p><p className="text-3xl font-bold text-green-700">{trends.summary?.symptoms_improving || 0}</p></CardContent></Card>
            <Card className="bg-red-50 border-red-200"><CardContent className="p-4 text-center"><p className="text-sm text-red-600">Worsening</p><p className="text-3xl font-bold text-red-700">{trends.summary?.symptoms_worsening || 0}</p></CardContent></Card>
            <Card><CardContent className="p-4 text-center"><p className="text-sm text-gray-500">Best Controlled</p><p className="text-lg font-bold text-teal-700">{SYMPTOM_CONFIG[trends.summary?.best_controlled]?.name || 'N/A'}</p></CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle>14-Day Symptom Trends</CardTitle></CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis domain={[0, 10]} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="pain" stroke="#ef4444" name="Pain" />
                    <Line type="monotone" dataKey="fatigue" stroke="#eab308" name="Fatigue" />
                    <Line type="monotone" dataKey="anxiety" stroke="#8b5cf6" name="Anxiety" />
                    <Line type="monotone" dataKey="mood" stroke="#ec4899" name="Mood" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Symptom Details</CardTitle></CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(trends.trends || {}).map(([key, data]) => {
                  const config = SYMPTOM_CONFIG[key];
                  if (!config) return null;
                  const Icon = config.icon;
                  return (
                    <div key={key} className="p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2"><Icon className="h-5 w-5" style={{ color: config.color }} /><span className="font-medium">{data.name}</span></div>
                        <div className="flex items-center gap-1">{getTrendIcon(data.trend)}<span className={`text-sm ${data.trend === 'improving' ? 'text-green-600' : data.trend === 'worsening' ? 'text-red-600' : 'text-gray-500'}`}>{data.trend}</span></div>
                      </div>
                      <div className="flex justify-between text-sm"><span>Current Avg: <strong className={getSeverityColor(data.current_avg)}>{data.current_avg}</strong></span><span>Range: {data.min} - {data.max}</span></div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="space-y-4">
          {history.length > 0 ? history.map((entry, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium">{new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(entry.symptoms || {}).map(([key, value]) => {
                    const config = SYMPTOM_CONFIG[key];
                    if (!config) return null;
                    return <Badge key={key} variant="outline" className={`${value <= 3 ? 'border-green-300 bg-green-50 text-green-700' : value <= 6 ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-red-300 bg-red-50 text-red-700'}`}>{config.name}: {value}</Badge>;
                  })}
                </div>
              </CardContent>
            </Card>
          )) : (
            <Card><CardContent className="p-8 text-center"><Calendar className="h-12 w-12 mx-auto text-gray-300 mb-3" /><h3 className="font-semibold text-gray-600">No History Yet</h3><p className="text-sm text-gray-500">Start logging your symptoms to see history</p></CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
};

export default SymptomDiaryEnhanced;
